export const SPRINTS = [
  {
    id: 'sprint-1',
    num: '01',
    week: 'Weeks 1–4',
    title: 'Foundations & the AI-App Mindset',
    meta: '4 weeks · 10 live sessions',
    lead: 'Learn how AI applications differ from traditional software and master the LLM building blocks — with a hands-on ML/DL refresher.',
    pts: [
      'How AI apps differ — non-determinism and the build-examine-decide loop',
      'LLM foundations: tokenization, context windows, sampling, caching, tool calling, multimodal inputs',
      'Prompt & context engineering; structured (JSON) outputs and validation',
      'Design patterns for AI apps — and when not to use AI/agents',
      'Evaluation 101: the error-analysis loop and a first eval harness',
      'ML/DL refresher (bias/variance, metrics, transformers) and a computer-vision primer',
      'System-design recap for AI apps: API design, data models, auth, async',
    ],
    chips: [
      'LLM Foundations',
      'Prompt Engineering',
      'Context Engineering',
      'Structured Outputs',
      'Design Patterns',
      'Evals 101',
      'ML Refresher',
      'CV Primer',
    ],
    ships: [
      {
        id: 'p1',
        name: 'PromptForge',
        type: 'mini project',
        desc: 'Structured-output extractor / prompt playground with schema validation.',
      },
      {
        id: 'p2',
        name: 'ModelSwitch',
        type: 'mini project',
        desc: 'Multi-model router with a first eval harness measuring tokens, latency, cost and quality.',
      },
      {
        id: 'p3',
        name: 'SnapClassify',
        type: 'mini project',
        desc: 'Transfer-learning image classifier deployed as a web app.',
      },
    ],
  },
  {
    id: 'sprint-2',
    num: '02',
    week: 'Weeks 5–8',
    title: 'Grounding with Data — RAG',
    meta: '4 weeks · 10 live sessions',
    lead: 'Ground models in real data — the most widely deployed production pattern — with retrieval-engineering depth.',
    pts: [
      'Embeddings, vector search and index types; storage tiers and when to pick each',
      'Chunking strategies and parsing real documents (PDF/HTML/tables) into LLM-ready inputs',
      'Hybrid search — dense + sparse fused with Reciprocal Rank Fusion; re-ranking',
      'RAG end-to-end with citations and groundedness; agentic RAG (query rewriting, multi-hop)',
      'Beyond vectors: knowledge graphs and a semantic layer over SQL',
      'Data engineering for AI — pipelines, quality & freshness, incremental sync',
      'Retrieval evaluation — recall@k, faithfulness, golden sets, eval-in-CI',
    ],
    chips: [
      'Embeddings',
      'Chunking',
      'Hybrid Search',
      'Re-ranking',
      'RAG',
      'Agentic RAG',
      'Knowledge Graphs',
      'Retrieval Evals',
    ],
    ships: [
      {
        id: 'p4',
        name: 'FindIt',
        type: 'mini project',
        desc: 'Dense + sparse search fused with RRF and a reranker over a real dataset.',
      },
      {
        id: 'b1',
        name: 'AskDocs',
        type: 'guided app',
        desc: 'Cited Q&A over a real doc source (Notion / Google Drive) with hybrid retrieval + reranking, conversation memory and guardrails. Ships as a live assistant + eval report + cost/latency dashboard.',
      },
    ],
  },
  {
    id: 'sprint-3',
    num: '03',
    week: 'Weeks 9–12',
    title: 'Agents, Protocols, MCP & Enterprise Integration',
    meta: '4 weeks · 10 live sessions',
    lead: 'Move from single LLM calls to agents that act — reliably and safely — over real enterprise systems.',
    pts: [
      'The agent loop; workflows vs agent harness; tool/function calling in depth',
      'MCP servers — multi-tenant scoping, RBAC at the tool boundary, audit logs',
      'Agent design patterns (ReAct, plan-and-execute, reflection, router); memory & context',
      'Multi-agent orchestration; vendor agent SDKs compared with LangGraph',
      'Agent communication protocols (A2A/ACP); typed schemas & state machines for reliable messaging',
      'Human-in-the-loop, guardrails, safe write-actions & permissions; agent evaluation & tracing',
      'Integration over real systems: OAuth/SSO, webhooks; a text-to-SQL agent with a verification loop',
    ],
    chips: [
      'Agent Loop',
      'Tool Calling',
      'MCP',
      'RBAC & Audit',
      'Multi-Agent',
      'A2A / ACP',
      'Human-in-the-Loop',
      'Agent Evals',
    ],
    ships: [
      {
        id: 'p5',
        name: 'ToolAgent',
        type: 'mini project',
        desc: 'A ReAct agent calling a live API.',
      },
      {
        id: 'p6',
        name: 'GateKeeper MCP',
        type: 'mini project',
        desc: 'A multi-tenant MCP server with RBAC, per-tenant scoping and audit logs.',
      },
      {
        id: 'b2',
        name: 'TriageOps',
        type: 'guided app',
        desc: 'Supervisor + specialist agents that classify tickets, draft grounded replies and route/escalate — via your multi-tenant MCP server, with human approval on writes. Ships as a deployed agent + trace/eval console.',
      },
    ],
  },
  {
    id: 'sprint-4',
    num: '04',
    week: 'Weeks 13–16',
    title: 'Eval-Driven Development, Fine-Tuning & Coding Agents I',
    meta: '4 weeks · 10 live sessions',
    lead: 'Make quality measurable, customise models when it pays, and start using coding agents properly.',
    pts: [
      'Deterministic vs LLM-as-judge vs human evaluation; building eval sets from traces',
      'Regression testing, evals as a CI deploy gate, evaluate-your-evals',
      'Prompt vs RAG vs fine-tune; the PEFT landscape (LoRA, QLoRA, adapters)',
      'Hands-on fine-tuning with 4-bit quantisation and before/after evaluation',
      'Post-training concepts — DPO, RL-style methods, reasoning models',
      'Distillation, quantisation and serving fine-tuned models',
      'Coding agents I — how a harness wraps an LLM; the plan → execute → deploy workflow',
    ],
    chips: [
      'LLM-as-Judge',
      'Deploy Gates',
      'LoRA / QLoRA',
      'DPO',
      'Distillation',
      'Quantisation',
      'Model Serving',
      'Coding Agents',
    ],
    ships: [
      {
        id: 'p7',
        name: 'EvalLab',
        type: 'mini project',
        desc: 'LLM-as-judge harness + a regression set wired in as a CI deploy gate.',
      },
      {
        id: 'p8',
        name: 'TinyTune',
        type: 'mini project',
        desc: 'LoRA/QLoRA fine-tune + before/after eval + a distillation demo.',
      },
      {
        id: 'b3',
        name: 'DomainCopilot',
        type: 'guided app',
        desc: 'Fine-tuned open model + RAG grounding, measured against the base model and a frontier API, with cost-vs-quality analysis; published to a model hub. Ships as your own domain model + app + eval report.',
      },
    ],
  },
  {
    id: 'sprint-5',
    num: '05',
    week: 'Weeks 17–20',
    title: 'Production, Cloud, Compliance & Real-Time',
    meta: '4 weeks · 10 live sessions',
    lead: 'Run AI reliably, cheaply, safely and compliantly; ship on a hyperscaler; build for real-time.',
    pts: [
      'Observability & tracing; drift, statistical regression testing, incident fire-drills',
      'Cost & performance — routing, semantic + prompt caching, token budgets, cost-per-query',
      'Security, governance & compliance literacy — injection, PII, guardrails, audit',
      'Deployment — containers, CI/CD with eval gates, versioning/rollbacks, scaling LLM APIs',
      'Cloud AI ecosystems — AWS Bedrock + AgentCore, SageMaker (Azure/GCP introduced)',
      'Hands-on AWS: productionise an earlier build with monitoring and cost alarms',
      'Real-time & voice — cascaded vs realtime speech, LiveKit, interrupt patterns, latency/cost',
    ],
    chips: [
      'Observability',
      'Drift & Incidents',
      'Cost & Latency',
      'Caching',
      'Security & Compliance',
      'CI/CD',
      'AWS Bedrock',
      'Voice Agents',
    ],
    ships: [
      {
        id: 'p9',
        name: 'RouteCache',
        type: 'mini project',
        desc: 'Model-router + semantic-cache gateway; cost/latency before-vs-after.',
      },
      {
        id: 'p10',
        name: 'SafeOps',
        type: 'mini project',
        desc: 'Tracing + PII redaction + a blocked prompt-injection + a drift alert.',
      },
      {
        id: 'b4',
        name: 'VoiceMate',
        type: 'guided app',
        desc: 'Speak-in / speak-out support agent with tool use and interrupt handling, tuned for latency and cost — with a justified cascaded-vs-realtime design choice. Ships as a live voice demo + latency/cost report.',
      },
      {
        id: 'b5',
        name: 'ShipIt',
        type: 'guided app',
        desc: 'Take an earlier build to AWS Bedrock/AgentCore or SageMaker with eval-gated CI/CD, monitoring, cost alarms, an incident fire-drill and a handover doc. Ships as "deployed on AWS" on your résumé — with the dashboard to prove it.',
      },
    ],
  },
  {
    id: 'sprint-6',
    num: '06',
    week: 'Weeks 21–24',
    title: 'Coding Agents II–III, Problem → Production & Capstone',
    meta: '4 weeks · 10 live sessions',
    lead: 'Master coding agents, own the path from problem to production, and ship your capstone.',
    pts: [
      'Coding agents II — autonomy levels, safe permissions, md-files (AGENTS.md/CLAUDE.md), hooks, MCP, parallel agents',
      'Coding agents III — behavioural + functional tests, agentic code review, security/architecture audits',
      'Problem → production I — framing the right problem, success metrics, the one-page spec, MVP vs careful build',
      'Problem → production II — building within real constraints: existing systems, data, security, tenancy; stack decisions',
      'Problem → production III — deploy, hand over & own: eval gates, fire-drills, runbooks, demoing to stakeholders',
      'Production-hardening clinic — multi-agent failure modes, RAG at scale, cost under load',
      'Capstone clinics and demo day',
    ],
    chips: [
      'Coding Agents',
      'md-files & Hooks',
      'Agentic Review',
      'Spec & Metrics',
      'Constraints',
      'Handover',
      'Hardening',
      'Capstone',
    ],
    ships: [
      {
        id: 'b6',
        name: 'RepoAgent',
        type: 'guided app',
        desc: 'Implement a real feature in a large open-source repo with coding agents — spec → plan → build → review — and build the evals that prove it works. Ships as a merge-quality PR + eval suite.',
      },
      {
        name: 'Capstone',
        type: 'your own product',
        desc: 'Choose from an industry menu or bring your own problem. Write the one-page spec and success metrics, build, deploy on AWS, evaluate, monitor — and tell the story on demo day.',
      },
    ],
    demo: 'closes the program — present your capstone live, its eval report and what you would do next.',
  },
];

export const INTERVIEW_SPRINT = {
  id: 'interview',
  icon: '★',
  week: 'Weeks 25–26',
  title: 'Interview Preparation Sprint',
  meta: '2 weeks · 6 live sessions + practice · after the six sprints',
  lead: 'Prepare for AI Engineer and Forward Deployed Engineer (FDE) interviews the way AI companies, GCCs and startups actually run them.',
  pts: [
    'Agentic-system-design rounds — research agents, text-to-SQL over large schemas, multi-agent coordination',
    'Ambiguous-problem decomposition round — clarify, decompose, propose a 4-week scope, name the riskiest assumption',
    'Behavioural preparation for engineering roles; communicating technical trade-offs',
    'Security & architecture interrogation with a question bank',
    'Portfolio walkthrough & architecture defence; résumé/LinkedIn review',
  ],
  chips: [
    'System Design',
    'Decomposition',
    'Behavioural',
    'Architecture Defence',
    'Portfolio',
  ],
  youGet:
    'Practice rounds with scored feedback, a reviewed résumé and LinkedIn, and a portfolio walkthrough you can defend — plus 5 referral interviews to SDE or AI Engineer roles.',
};

export const LADDER_ROWS = [
  {
    theme: 'LLM foundations & the AI-app mindset',
    mini: [
      { id: 'p1', code: 'P1', name: 'PromptForge' },
      { id: 'p2', code: 'P2', name: 'ModelSwitch' },
      { id: 'p3', code: 'P3', name: 'SnapClassify' },
    ],
    guided: [],
  },
  {
    theme: 'Grounding with data (RAG)',
    mini: [{ id: 'p4', code: 'P4', name: 'FindIt' }],
    guided: [{ id: 'b1', code: 'B1', name: 'AskDocs' }],
  },
  {
    theme: 'Agents, MCP & enterprise integration',
    mini: [
      { id: 'p5', code: 'P5', name: 'ToolAgent' },
      { id: 'p6', code: 'P6', name: 'GateKeeper MCP' },
    ],
    guided: [{ id: 'b2', code: 'B2', name: 'TriageOps' }],
  },
  {
    theme: 'Evaluation & fine-tuning; coding agents I',
    mini: [
      { id: 'p7', code: 'P7', name: 'EvalLab' },
      { id: 'p8', code: 'P8', name: 'TinyTune' },
    ],
    guided: [{ id: 'b3', code: 'B3', name: 'DomainCopilot' }],
  },
  {
    theme: 'Production, cloud, compliance, voice',
    mini: [
      { id: 'p9', code: 'P9', name: 'RouteCache' },
      { id: 'p10', code: 'P10', name: 'SafeOps' },
    ],
    guided: [
      { id: 'b4', code: 'B4', name: 'VoiceMate' },
      { id: 'b5', code: 'B5', name: 'ShipIt' },
    ],
  },
  {
    theme: 'Coding agents II–III; problem → production',
    mini: [],
    guided: [
      { id: 'b6', code: 'B6', name: 'RepoAgent' },
      { code: 'CAP', name: 'Capstone', noLink: true },
    ],
  },
  {
    theme: 'Optional self-builds',
    mini: [],
    guided: [
      { id: 'o1', code: 'O1', name: 'AutoClaims Assist' },
      { id: 'o2', code: 'O2', name: 'MedAuth Assist' },
      { id: 'o3', code: 'O3', name: 'CallReview' },
      { id: 'o4', code: 'O4', name: 'QueryPilot' },
    ],
  },
];

export const QUICK_REF = [
  {
    code: 'P1',
    id: 'p1',
    name: 'PromptForge',
    oneLiner: 'Turns messy text into clean, validated JSON',
    skill: 'Prompt & context engineering, structured outputs',
  },
  {
    code: 'P2',
    id: 'p2',
    name: 'ModelSwitch',
    oneLiner: 'Routes requests across 4 models and measures each',
    skill: 'Model routing, first evals, cost/latency',
  },
  {
    code: 'P3',
    id: 'p3',
    name: 'SnapClassify',
    oneLiner: 'Image classifier deployed as a web app',
    skill: 'Deep-learning & computer-vision basics',
  },
  {
    code: 'P4',
    id: 'p4',
    name: 'FindIt',
    oneLiner: 'Hybrid keyword + semantic search with a reranker',
    skill: 'Retrieval engineering',
  },
  {
    code: 'P5',
    id: 'p5',
    name: 'ToolAgent',
    oneLiner: 'An agent that calls a live API to get things done',
    skill: 'Tool calling, the agent loop',
  },
  {
    code: 'P6',
    id: 'p6',
    name: 'GateKeeper MCP',
    oneLiner: 'Secure, multi-tenant tool server for agents',
    skill: 'MCP, permissions (RBAC), audit logs',
  },
  {
    code: 'P7',
    id: 'p7',
    name: 'EvalLab',
    oneLiner: 'Automated quality gate that blocks bad releases',
    skill: 'Evaluation-driven development, CI gates',
  },
  {
    code: 'P8',
    id: 'p8',
    name: 'TinyTune',
    oneLiner: 'Fine-tunes a small model and proves it improved',
    skill: 'Fine-tuning, quantisation, distillation',
  },
  {
    code: 'P9',
    id: 'p9',
    name: 'RouteCache',
    oneLiner: 'Gateway that cuts LLM cost and latency',
    skill: 'Caching, routing, cost engineering',
  },
  {
    code: 'P10',
    id: 'p10',
    name: 'SafeOps',
    oneLiner: 'Tracing, PII redaction, injection defence, drift alert',
    skill: 'Observability, security, guardrails',
  },
  {
    code: 'B1',
    id: 'b1',
    name: 'AskDocs',
    oneLiner: "Cited Q&A assistant over a company's real documents",
    skill: 'Production RAG end-to-end',
  },
  {
    code: 'B2',
    id: 'b2',
    name: 'TriageOps',
    oneLiner: 'Multi-agent support operations over a real helpdesk',
    skill: 'Enterprise agents, MCP, human-in-the-loop',
  },
  {
    code: 'B3',
    id: 'b3',
    name: 'DomainCopilot',
    oneLiner: 'Fine-tuned industry copilot vs a frontier model',
    skill: 'Fine-tuning + RAG + cost/quality analysis',
  },
  {
    code: 'B4',
    id: 'b4',
    name: 'VoiceMate',
    oneLiner: 'Real-time voice agent that can act',
    skill: 'Voice AI, latency engineering',
  },
  {
    code: 'B5',
    id: 'b5',
    name: 'ShipIt',
    oneLiner: 'An earlier build productionised on AWS',
    skill: 'Cloud deployment, CI/CD, monitoring, handover',
  },
  {
    code: 'B6',
    id: 'b6',
    name: 'RepoAgent',
    oneLiner: 'A real feature shipped into a large codebase with coding agents',
    skill: 'Coding agents, agentic review, evals',
  },
  {
    code: 'CAP',
    name: 'Capstone',
    oneLiner: 'Your own AI product, deployed on AWS',
    skill: 'Problem → production, end to end',
    noLink: true,
  },
  {
    code: 'O1',
    id: 'o1',
    name: 'AutoClaims Assist',
    oneLiner: 'Insurance claims triage with human review',
    skill: 'Regulated multi-agent decisioning',
  },
  {
    code: 'O2',
    id: 'o2',
    name: 'MedAuth Assist',
    oneLiner: 'Healthcare prior-authorisation co-pilot',
    skill: 'Compliance-first co-pilot',
  },
  {
    code: 'O3',
    id: 'o3',
    name: 'CallReview',
    oneLiner: 'Call-centre QA & compliance pipeline',
    skill: 'Speech-analytics pipeline',
  },
  {
    code: 'O4',
    id: 'o4',
    name: 'QueryPilot',
    oneLiner: 'Reliable text-to-SQL over a 200-table warehouse',
    skill: 'Agents over structured data',
  },
];

export default SPRINTS;
